<script setup lang="ts">
import {
  TirCheckbox,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmDatepicker,
  TirPmDropdown,
  TirPmIconView,
  TirPmSearchInput,
} from 'tir-components';
import { ArrowDownTrayIcon } from 'tir-style-system/icons/outline';
import { computed, nextTick, PropType, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { MonitoringPagesEnum } from '../enums';
import GetEmployeesWithAccessToProjectItemModel from '../models/GetEmployeesWithAccessToProjectItemModel';
import { MonitoringHeaderFilterModel } from '../models';

const router = useRouter();

const props = defineProps({
  /**Фильтр */
  filter: { type: Object as PropType<MonitoringHeaderFilterModel> },
  /**Поле поиск */
  search: { type: String },
  /**Поле поиска исполнителей */
  searchFieldPerformers: { type: String },
  /**Исполнители */
  performers: { type: Array as PropType<GetEmployeesWithAccessToProjectItemModel[]> },
  /**Общее затраченное время */
  totalWorkTime: { type: Number },
  /**Дата начала */
  dateStart: { type: Date },
  /**Дата окончания */
  dateEnd: { type: Date },
});

const emit = defineEmits<{
  /**При поиске тикетов */
  (e: 'update:search', text: string);
  /**При поиске исполнителей */
  (e: 'update:searchPerformers', text: String);
  /**При выборе начальной даты */
  (e: 'update:selectedDateStart', date: Date);
  /**При выборе конечной даты */
  (e: 'update:selectedDateEnd', date: Date);
  /**При выборе исполнителя */
  (e: 'selectPerformer', performer: GetEmployeesWithAccessToProjectItemModel);
  /**При получении всего списка тикетов */
  (e: 'clearFilter');
}>();

/**Строка поиска исполнителей*/
const searchBarPerformers = ref();

/**Поиск тикетов */
const innerSearch = computed({
  get: () => props.search,
  set: (value) => emit('update:search', value),
});
/**Выбранный мониторинг */
const selectMonitoringPage = computed(() => router.currentRoute.value.name);
/**Поиск исполнителей */
const searchPerformers = computed({
  get: () => searchBarPerformers.value,
  set: (value) => {
    emit('update:searchPerformers', value);
    searchBarPerformers.value = value;
  },
});
/**Поиск в зависимости от мониторинга */
const searchField = computed({
  get() {
    if (selectMonitoringPage.value === MonitoringPagesEnum.Days) {
      return searchPerformers.value;
    } else if (selectMonitoringPage.value === MonitoringPagesEnum.Tickets) {
      return innerSearch.value;
    }
    return '';
  },
  set(val: string) {
    if (selectMonitoringPage.value === MonitoringPagesEnum.Days) {
      searchPerformers.value = val;
    } else if (selectMonitoringPage.value === MonitoringPagesEnum.Tickets) {
      innerSearch.value = val;
    }
  },
});

/**Отображения label в зависимости от мониторинга */
const label = computed(() => {
  if (selectMonitoringPage.value === MonitoringPagesEnum.Days) {
    return 'Поиск по ФИО';
  } else if (selectMonitoringPage.value === MonitoringPagesEnum.Tickets) {
    return 'Поиск по тикету';
  }
  return '';
});
/**Общее затраченное время */
const totalWorkTime = computed(() => {
  const workTime = props.totalWorkTime.toFixed(2);

  const hours = workTime.split('.')[0].padStart(2, '0');
  const minutes = workTime.split('.')[1].padStart(2, '0');

  return `${hours}ч ${minutes}мин`;
});
/**Выбор начальной даты */
const selectedDateStart = computed({
  get: () => props.dateStart,
  set: (value: Date) => {
    if (value > props.dateEnd) return;
    emit('update:selectedDateStart', value);
  },
});

/**Выбор конечной даты */
const selectedDateEnd = computed({
  get: () => props.dateEnd,
  set: (value: Date) => {
    if (value < props.dateStart) return;
    emit('update:selectedDateEnd', value);
  },
});

/**Отслеживание изменение пути */
watch(
  () => router.currentRoute.value.path,
  () => {
    searchBarPerformers.value = '';
  },
);

/**Изменение состояния чекбокса Все мои проекты */
const changeAllProjectsChecked = (state: boolean) => {
  localStorage.setItem('stateAllMyProjectsChecked', String(state));
  props.filter.AllMyProjectsChecked = state;
};
/**Выбор исполнителя */
const selectPerformer = (performer: GetEmployeesWithAccessToProjectItemModel) => {
  emit('selectPerformer', performer);
  searchBarPerformers.value = `${performer.LastName} ${performer.FirstName} ${performer.MiddleName}`;
};

/**Получить полный список тикетов */
const getFullList = () => {
  searchBarPerformers.value = '';
  emit('clearFilter');
};
</script>

<template>
  <div class="monitoring__header">
    <div class="monitoring__header--left-part">
      <TirPmSearchInput
        :label="label"
        isSmall
        v-model="filter.Search"
        :isWithPopup="false"
        @onClearSearchInput="getFullList"
        placeholder=""
      />
      <TirPmSearchInput
        v-if="selectMonitoringPage === MonitoringPagesEnum.Tickets"
        label="Исполнитель"
        isSmall
        v-model="searchPerformers"
        @onClearSearchInput="getFullList"
        placeholder=""
      >
        <template #content>
          <div
            class="monitoring__header--dropdown-content"
            v-for="performer in performers"
            :key="performer.UserId"
            @click="selectPerformer(performer)"
          >
            <TirPmIconView
              :size="24"
              isRounded
              :isBordered="false"
              :colorCode="performer.AvatarColor"
              :imageLoadUrl="performer.Foto"
            >
              <template #mainSlot>
                {{ performer.FirstName[0] }}{{ performer.LastName[0] }}
              </template>
            </TirPmIconView>
            {{ performer.LastName }} {{ performer.FirstName }} {{ performer.MiddleName }}
          </div>
        </template>
      </TirPmSearchInput>

      <div class="monitoring__header--datepicker-block">
        <TirPmDatepicker
          class="monitoring__header--datepicker"
          :input="{ label: 'Дата начала', isMicro: true }"
          v-model="selectedDateStart"
        />
        <TirPmDatepicker
          class="monitoring__header--datepicker"
          :input="{ label: 'Дата конца', isMicro: true }"
          v-model="selectedDateEnd"
        />
      </div>
      <TirPmButton
        :size="TirPmButtonSizeEnum.Small"
        :variant="TirPmButtonVariantEnum.SecondaryState"
      >
        <template #leftSlot>
          <ArrowDownTrayIcon />
        </template>
      </TirPmButton>
      <div
        v-if="selectMonitoringPage === MonitoringPagesEnum.Days"
        class="monitoring__header--checkbox"
      >
        <TirCheckbox
          v-model="filter.AllMyProjectsChecked"
          @update:modelValue="changeAllProjectsChecked"
        >
          <template #mainSign>
            <div>Все мои проекты</div>
          </template>
        </TirCheckbox>
      </div>
    </div>
    <div class="monitoring__header--total-time-spent">
      Общее время затрачено —  {{ totalWorkTime }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.monitoring {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    &--checkbox {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    &--search-performers {
      text-overflow: ellipsis;
      z-index: 1000;
    }

    &--dropdown-content {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem;
      cursor: pointer;
      &:hover {
        background-color: var(--bg-secondary, #f8f6f9);
      }
    }

    &--left-part {
      display: flex;
      align-items: center;
      gap: 1.5rem;
    }

    &--dropdown {
      height: 3rem;
    }
    &--datepicker-block {
      display: flex;
      align-items: center;
      gap: 1.5rem;
    }
    &--datepicker {
      min-width: 11.938rem !important;
    }
    &--icon {
      width: 1.5rem;
      height: 1.5rem;
    }
    &--total-time-spent {
      padding: 0.25rem 0.5rem 0.25rem 0.5rem;
      border: 0.125rem solid var(--text-info, #2a77ef);
      border-radius: 0.5rem;
      color: var(--text-info, #2a77ef);
      font-size: 1.125rem;
      font-weight: 500;
      white-space: nowrap;
      margin-left: 0.5rem;
    }
  }
}
</style>

<style lang="scss">
.monitoring__header--datepicker .picker-container__target .input .input-container {
  width: 11.938rem;
  // height: 3rem;
}

.monitoring__header--dropdown .tir-popover_element .tir-pm-dropdown__target .tir-pm-dropdown__area {
  height: 3rem;
  .tir-pm-dropdown__desc-select {
    min-height: 3rem !important;
  }
}

.monitoring__header--search-performers
  .tir-pm-search-input__input-container
  .tir-pm-search-input__input
  .input-container__input-wrapper
  .input-container__input {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
